/**
 * Leadler: sub-tabs Harita (scan cells), Huni (lead funnel) and Öğrenme (the learning loop,
 * design section 4), plus the learning batch detail screen (/leadler/ogrenme/paket). Data
 * loads when the screen opens; against the real API the read models are not served yet and
 * the screen says so ("sunucuda henüz yok"), as Ayarlar does.
 */

import { REJECT_REASON_CODES } from '@twin/contracts';
import { type ReactNode, useEffect, useState } from 'react';
import {
  CHANGE_CLASS_LABELS,
  type LearningBatch,
  type LearningDecision,
  type LearningItemDecision,
  type LearningProposal,
  LOOP_LABELS,
  TERM_SLOT_LABELS,
} from '../api/learning.js';
import { approvableInPanel, batchViolation } from '../api/learning-rules.js';
import { MIN_REASON_LENGTH, reasonText } from '../api/policy-rules.js';
import { REJECT_REASON_LABELS, type RejectReasonCode } from '../api/types.js';
import { usePanel, useStore } from '../app/context.js';
import { useLeads } from '../app/leads.js';
import { useIsDesktop } from '../app/media.js';
import { LEADS_TABS, type LeadsTab, Link, navigate } from '../app/router.js';
import { POLICY_HOLD_MS, T4HoldButton } from '../components/HoldButton.js';
import { Icon } from '../components/icons.js';
import { LeadMap } from '../components/leads-dashboard.js';
import { LeadFunnel } from '../components/leads-map.js';
import { ClassChip, EstimateValue, LearningPage } from '../components/learning.js';
import { ChoiceChips } from '../components/Sheet.js';
import { AppBar, BottomTabBar, DetailAppBar, StatusArea, TopBar, TopBarStatus } from '../components/shell.js';
import { formatDayMonth, formatLongDate, formatNumber } from '../lib/format.js';

const TAB_LABELS: Record<LeadsTab, string> = { harita: 'Harita', huni: 'Huni', ogrenme: 'Öğrenme' };

function LeadsTabs({ tab }: { tab: LeadsTab }) {
  return (
    <nav className="lr-tabs" aria-label="Leadler bölümleri">
      {LEADS_TABS.map((id) => (
        <Link
          key={id}
          to={`/leadler/${id}`}
          className={id === tab ? 'lr-tabs__item lr-tabs__item--on' : 'lr-tabs__item'}
          aria-current={id === tab ? 'page' : undefined}
        >
          {TAB_LABELS[id]}
        </Link>
      ))}
    </nav>
  );
}

/** Loading, or why the data did not come (not served by the live API yet) with a retry. */
function LoadState({ error, what }: { error: string | null; what: string }) {
  const { store } = useLeads();
  if (!error) {
    return (
      <div className="loading" role="status">
        Yükleniyor…
      </div>
    );
  }
  return (
    <div className="loading lr-unserved" role="status">
      {what} yüklenemedi: {error}{' '}
      <button type="button" className="text-btn" onClick={() => void store.reload()}>
        Tekrar dene
      </button>
    </div>
  );
}

function TabBody({ tab }: { tab: LeadsTab }) {
  const { state } = useLeads();
  if (tab === 'ogrenme') {
    return state.learning ? (
      <LearningPage view={state.learning} />
    ) : (
      <LoadState error={state.learningError} what="Öğrenme" />
    );
  }
  if (!state.leads) return <LoadState error={state.leadsError} what="Leadler" />;
  return tab === 'harita' ? <LeadMap view={state.leads} /> : <LeadFunnel view={state.leads} />;
}

function useLoadLeads() {
  const { store } = useLeads();
  useEffect(() => {
    void store.ensureLoaded();
  }, [store]);
}

export function Leadler({ tab, batch }: { tab: LeadsTab; batch: boolean }) {
  useLoadLeads();
  if (batch) return <BatchScreen />;
  return <LeadlerTabs tab={tab} />;
}

function LeadlerTabs({ tab }: { tab: LeadsTab }) {
  const state = usePanel();
  const desktop = useIsDesktop();
  if (desktop) {
    return (
      <>
        <TopBar title="Leadler" subtitle={`Satış · ${TAB_LABELS[tab]}`} status={<TopBarStatus />} />
        <main className="desk-main lr-desk">
          <LeadsTabs tab={tab} />
          <TabBody tab={tab} />
        </main>
      </>
    );
  }
  return (
    <>
      <AppBar title="Leadler" subtitle={`Satış · ${formatLongDate(state.now)}`} />
      <StatusArea />
      <main className="phone-main">
        <h1 className="sr-only">Leadler · {TAB_LABELS[tab]}</h1>
        <Link to="/diger" className="back-link">
          <Icon name="chevron-left" size={18} />
          Diğer
        </Link>
        <LeadsTabs tab={tab} />
        <TabBody tab={tab} />
      </main>
      <BottomTabBar active="diger" />
    </>
  );
}

// ---- the learning batch (detail screen: the only place a learning item is decided) ----

interface Draft {
  decision: LearningDecision;
  reasonCode: RejectReasonCode | null;
  itemHoldConfirmed: boolean;
}

const DECISION_LABELS: Record<LearningDecision, string> = {
  approve: 'Kabul',
  reject: 'Reddet',
  defer: 'Sonraya bırak',
};

function initialDrafts(batch: LearningBatch): Record<string, Draft> {
  return Object.fromEntries(
    batch.proposals.map((proposal) => [
      proposal.proposalId,
      { decision: 'defer', reasonCode: null, itemHoldConfirmed: false } satisfies Draft,
    ]),
  );
}

function ProposalCard({
  proposal,
  draft,
  onChange,
  blocker,
}: {
  proposal: LearningProposal;
  draft: Draft;
  onChange: (draft: Draft) => void;
  blocker: string | null;
}) {
  const panel = useStore();
  const h3 = proposal.changeClass === 'H3';
  const options: LearningDecision[] =
    approvableInPanel(proposal) && !h3 ? ['approve', 'reject', 'defer'] : ['reject', 'defer'];
  const choiceValue = h3 && draft.decision === 'approve' ? null : draft.decision;
  const keyword = proposal.keyword;
  return (
    <article className="lr-card" aria-labelledby={`lp-${proposal.proposalId}`}>
      <header className="lr-card__head">
        <ClassChip changeClass={proposal.changeClass} />
        <span className="key ink-2">
          {proposal.loop} · {LOOP_LABELS[proposal.loop]}
        </span>
      </header>
      <h2 id={`lp-${proposal.proposalId}`} className="lr-card__title">
        {proposal.title}
      </h2>
      <dl className="lr-card__facts">
        <div>
          <dt>Ne değişir</dt>
          <dd>{proposal.what}</dd>
        </div>
        <div>
          <dt>Kanıt</dt>
          <dd>
            {proposal.evidenceText}
            {proposal.evidence ? (
              <>
                {' '}
                · <EstimateValue estimate={proposal.evidence} unit="share" compact />
              </>
            ) : null}
          </dd>
        </div>
        {keyword ? (
          <div>
            <dt>Terim</dt>
            <dd>
              <span lang="en" className="mono">
                {keyword.term}
              </span>{' '}
              · {TERM_SLOT_LABELS[keyword.slot]} · {keyword.locale} ·{' '}
              <span className="lr-region">
                <Icon name="map-pin" size={12} />
                {keyword.region}
              </span>{' '}
              · lift <span className="mono">{formatNumber(keyword.lift, 1)}</span> · df{' '}
              <span className="mono">
                {keyword.dfQ}/{keyword.nQ}
              </span>{' '}
              ile{' '}
              <span className="mono">
                {keyword.dfN}/{keyword.nN}
              </span>{' '}
              · <span className="mono">{keyword.businessCategories}</span> iş kategorisi
              {keyword.vehicleMapped === true ? ' · katalog aracı' : ''}
              <span className="lr-card__domains mono">{keyword.exampleDomains.join(' · ')}</span>
              {keyword.biasNote ? <span className="lr-card__bias warning">{keyword.biasNote}</span> : null}
            </dd>
          </div>
        ) : null}
        <div>
          <dt>Beklenen etki</dt>
          <dd>{proposal.expectedEffect}</dd>
        </div>
        <div>
          <dt>Risk</dt>
          <dd>{proposal.risk}</dd>
        </div>
        <div>
          <dt>Geri alınır mı</dt>
          <dd>{proposal.reversible ? 'Evet' : 'Hayır'}</dd>
        </div>
        <div>
          <dt>Pazartesi ölçüsü</dt>
          <dd>{proposal.judgedBy}</dd>
        </div>
      </dl>
      {proposal.changeClass === 'H2' ? (
        <p className="lr-note lr-note--info">
          <Icon name="git-pull-request" size={14} className="lr-note__icon" />
          <span>
            Sürümle gelir: {proposal.releasePr ? `PR ${proposal.releasePr}` : 'sürüm PR’ı'} birleşip v* etiketiyle
            yayınlanınca uygulanır. Panelden onaylanmaz.
          </span>
        </p>
      ) : null}
      <div className="lr-card__decide">
        <ChoiceChips
          label={`${proposal.title}: karar`}
          options={options}
          labels={DECISION_LABELS}
          value={choiceValue}
          onChange={(decision) => onChange({ decision, reasonCode: null, itemHoldConfirmed: false })}
        />
        {h3 ? (
          draft.decision === 'approve' && draft.itemHoldConfirmed ? (
            <p className="lr-card__held">
              <Icon name="lock" size={14} />
              Kabul · T4 basılı tutuldu
            </p>
          ) : (
            <T4HoldButton
              compact
              label="Kabul et"
              sublabel={`T4 · ${CHANGE_CLASS_LABELS.H3}`}
              holdVerb="kabul etmek"
              disabledReason={blocker}
              onComplete={() => onChange({ decision: 'approve', reasonCode: null, itemHoldConfirmed: true })}
              onEarlyRelease={() => panel.showToast('T4 öneriyi kabul için 1,5 sn basılı tut', { icon: 'info' })}
            />
          )
        ) : null}
      </div>
      {draft.decision === 'reject' ? (
        <ChoiceChips
          label={`${proposal.title}: ret gerekçesi`}
          options={REJECT_REASON_CODES}
          labels={REJECT_REASON_LABELS}
          value={draft.reasonCode}
          onChange={(reasonCode) => onChange({ ...draft, reasonCode })}
        />
      ) : null}
    </article>
  );
}

function BatchBody({ batch }: { batch: LearningBatch }) {
  const { store, state } = useLeads();
  const panel = usePanel();
  const panelStore = useStore();
  const [drafts, setDrafts] = useState<Record<string, Draft>>(() => initialDrafts(batch));
  const [reason, setReason] = useState('');
  const decisions: LearningItemDecision[] = batch.proposals.map((proposal) => {
    const draft = drafts[proposal.proposalId] ?? { decision: 'defer', reasonCode: null, itemHoldConfirmed: false };
    return { proposalId: proposal.proposalId, ...draft };
  });
  const approving = decisions.some((item) => item.decision === 'approve');
  const count = (decision: LearningDecision) => decisions.filter((item) => item.decision === decision).length;
  const blocker = state.busy ? 'Kaydediliyor…' : store.decisionBlocker();
  // Every rule except the hold itself, which the button provides.
  const refused = batchViolation(
    batch,
    { batchId: batch.batchId, payloadHash: batch.payloadHash, decisions, reason, confirmedByHold: true },
    panel.now,
  );
  const disabledReason = blocker ?? refused?.message ?? null;
  const submit = async (confirmedByHold: boolean) => {
    const result = await store.decideBatch(decisions, { reason, confirmedByHold });
    if (result) navigate('/leadler/ogrenme');
  };
  return (
    <>
      <p className="lr-batch__sum">
        <span className="mono">{batch.week}</span> · <span className="mono">{batch.proposals.length}</span> öneri ·
        tahmini <span className="mono">{batch.estimatedMinutes}</span> dk · hedef{' '}
        <span className="mono">{batch.targetMinutes}</span> dk · son gün{' '}
        <span className="mono">{formatDayMonth(batch.expiresAt)}</span>
      </p>
      <p className="placeholder-text">
        Her kartta ne değiştiği, kanıtı ve pazartesi hangi ölçüyle yargılanacağı yazar. Karar vermediğin öneri sonraki
        haftaya kalır. Kabul etmek aramayı veya gönderimi genişletir: tek gerekçe ve 1,5 sn basılı tutma ister; T4 öneri
        ayrıca kendi düğmesini ister.
      </p>
      {batch.proposals.map((proposal) => (
        <ProposalCard
          key={proposal.proposalId}
          proposal={proposal}
          draft={drafts[proposal.proposalId] ?? { decision: 'defer', reasonCode: null, itemHoldConfirmed: false }}
          blocker={blocker}
          onChange={(draft) => setDrafts((current) => ({ ...current, [proposal.proposalId]: draft }))}
        />
      ))}
      <section className="lr-batch__submit" aria-labelledby="lr-batch-karar">
        <h2 id="lr-batch-karar" className="lr-sub">
          Karar
        </h2>
        <p className="lr-batch__counts" aria-live="polite">
          <span className="mono">{count('approve')}</span> kabul · <span className="mono">{count('reject')}</span> ret ·{' '}
          <span className="mono">{count('defer')}</span> sonraya
        </p>
        {approving ? (
          <label className="field">
            <span className="field__label">
              Gerekçe · zorunlu (en az {MIN_REASON_LENGTH} harf), onay kaydına yazılır
            </span>
            <textarea
              className="field__input"
              rows={3}
              value={reason}
              aria-required="true"
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
        ) : null}
        {approving ? (
          <T4HoldButton
            label="Kararları kaydet"
            sublabel="1,5 sn basılı tut · kabuller gerekçesiyle kayda geçer"
            holdVerb="onaylamak"
            durationMs={POLICY_HOLD_MS}
            disabledReason={disabledReason}
            onComplete={() => void submit(true)}
            onEarlyRelease={() => panelStore.showToast('Kabul için 1,5 sn basılı tut', { icon: 'info' })}
          />
        ) : (
          <button
            type="button"
            className="btn btn--primary lr-batch__save"
            disabled={disabledReason !== null}
            onClick={() => void submit(false)}
          >
            {disabledReason ?? 'Kararları kaydet'}
          </button>
        )}
        {reasonText(reason).length > 0 && reasonText(reason).length < MIN_REASON_LENGTH ? (
          <p className="ink-2">Gerekçe biraz daha uzun olmalı.</p>
        ) : null}
      </section>
    </>
  );
}

function BatchContent() {
  const { state } = useLeads();
  const learning = state.learning;
  if (!learning) return <LoadState error={state.learningError} what="Öğrenme" />;
  const batch = learning.batch;
  if (!batch || batch.proposals.length === 0) {
    return (
      <>
        <p className="placeholder-text">Bekleyen öğrenme paketi yok.</p>
        <Link to="/leadler/ogrenme" className="btn btn--secondary">
          Öğrenme'ye dön
        </Link>
      </>
    );
  }
  // A new payload hash (the batch changed on the server) starts the decisions over.
  return <BatchBody key={batch.payloadHash} batch={batch} />;
}

function BatchScreen() {
  const state = usePanel();
  const desktop = useIsDesktop();
  const { state: leads } = useLeads();
  const week = leads.learning?.batch?.week ?? leads.learning?.week ?? '';
  const content: ReactNode = <BatchContent />;
  if (desktop) {
    return (
      <>
        <TopBar title="Öğrenme paketi" subtitle={`Leadler › Öğrenme · ${week}`} status={<TopBarStatus />} />
        <main className="desk-main desk-main--narrow lr-batch">
          <Link to="/leadler/ogrenme" className="back-link">
            <Icon name="chevron-left" size={18} />
            Öğrenme
          </Link>
          {content}
        </main>
      </>
    );
  }
  return (
    <>
      <DetailAppBar
        back={{ label: 'Öğrenme', to: '/leadler/ogrenme' }}
        title="Öğrenme paketi"
        titleMono={null}
        segments={null}
        metaKey="Hedef"
        metaValue={`${leads.learning?.batch?.targetMinutes ?? 10} dk`}
      />
      <main className="phone-main lr-batch">
        <h1 className="sr-only">Öğrenme paketi {formatLongDate(state.now)}</h1>
        {content}
      </main>
    </>
  );
}
